import {
  ChartOptions,
  ChartTypeRegistry,
  TimeUnit,
  TooltipItem,
} from 'chart.js';
import { STATS } from 'state/stats/type';
import CURRENCY from 'utils/currency';
import { getTooltipWeekRange } from 'utils/dashboard';
import { ORG_TIMEZONE_DEFAULT } from 'utils/constants';
import { formatToTz } from 'utils/date-format';
import { chartStyles } from '../chartConfig';
import * as util from 'utils/dashboard';
import { ICustomChartConfig } from './chartjs-custom';
import {
  getMetricValueFromPayType,
  formatValueToDisplayStr,
} from 'utils/chart';

export const getChartOptions = (
  activeFreq: STATS.IIntervalType,
  timeAxisStepSize: number,
  timeAxisUnit: TimeUnit,
  maxXTicksLimit: number,
  param: STATS.graphType,
  datasets: { type: STATS.IPaymentType; value: STATS.ValuePoint[] }[],
  timezone = ORG_TIMEZONE_DEFAULT
): ChartOptions<'line'> => {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  const adapter = require('chartjs-adapter-luxon').default; // skipcq: JS-0356

  return {
    interaction: {
      intersect: false,
      mode: 'index',
    },
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        intersect: false,
        backgroundColor: chartStyles.tooltipBgColor,
        titleColor: chartStyles.tooltipTextColor,
        titleFont: {
          size: chartStyles.tooltipFontSize,
          weight: 'bold',
        },
        bodyColor: chartStyles.tooltipTextColor,
        bodyFont: {
          size: chartStyles.tooltipFontSize,
          weight: 'normal',
        },
        footerColor: chartStyles.tooltipTextColor,
        footerMarginTop: 18,
        footerFont: {
          size: chartStyles.tooltipFontSize,
          weight: 'normal',
        },
        titleMarginBottom: chartStyles.tooltipTitleMarginBottom,
        padding: {
          top: chartStyles.tooltipPaddingY,
          right: chartStyles.tooltipPaddingX,
          bottom: chartStyles.tooltipPaddingY,
          left: chartStyles.tooltipPaddingX,
        },
        filter: (item, _, array) => {
          if (array?.length === 1) return true;
          return item.dataset?.paymentType != STATS.IPaymentType.ALL;
        },
        callbacks: {
          beforeBody: (tooltipItems) => {
            const visibleDatasets = tooltipItems[0].chart.data.datasets.filter(
              (_, i) => tooltipItems[0].chart.isDatasetVisible(i)
            );

            if (tooltipItems[0]?.chart.options?.plugins?.tooltip) {
              tooltipItems[0].chart.options.plugins.tooltip.displayColors =
                visibleDatasets.length > 1;
            }
            return '';
          },
          title: (tooltipItem) => {
            const date = new Date(tooltipItem?.[0]?.parsed?.x)?.toISOString();
            switch (activeFreq) {
              case 'hourly':
                return formatToTz(date, timezone, 'd MMM, hh:mm a');
              case 'weekly':
                return getTooltipWeekRange(date);
              case 'monthly':
                return formatToTz(date, timezone, 'd MMM yy');
              case 'daily':
              default:
                return formatToTz(date, timezone, 'd MMM');
            }
          },
          afterTitle: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>[]) => {
            const visibleDatasets = tooltipItem[0].chart.data.datasets.filter(
              (_, i) => tooltipItem[0].chart.isDatasetVisible(i)
            );
            const dataIndex = tooltipItem[0].dataIndex;
            const totalDataset = datasets.find(
              ({ type }) => type === STATS.IPaymentType.ALL
            );
            const dataObject = totalDataset?.value?.[dataIndex];
            const totalValue = dataObject
              ? getMetricValueFromPayType(
                  param,
                  dataObject?.gross_amount,
                  dataObject?.transaction_count
                )
              : 0;

            if (visibleDatasets.length > 1)
              return formatValueToDisplayStr(param, totalValue);
            return '';
          },
          label: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>) => {
            const datasetLabel = tooltipItem?.dataset?.label;
            const value = tooltipItem?.parsed?.y;

            const visibleDatasets = tooltipItem.chart.data.datasets.filter(
              (_, i) => tooltipItem.chart.isDatasetVisible(i)
            );
            const valueStr = formatValueToDisplayStr(param, value);

            if (visibleDatasets.length > 1) {
              return `${datasetLabel}: ${valueStr}`;
            } else {
              return `${valueStr}`;
            }
          },
          footer:
            param !== STATS.graphType.payments && param !== STATS.graphType.aov
              ? (tooltipItem: TooltipItem<keyof ChartTypeRegistry>[]) => {
                  const index = tooltipItem[0].dataIndex;
                  const paymentType = tooltipItem[0].dataset?.paymentType;
                  const data = datasets.find(
                    ({ type }) => type === paymentType
                  );
                  const point = data?.value?.[index];

                  const visibleDatasets =
                    tooltipItem[0].chart.data.datasets.filter((_, i) =>
                      tooltipItem[0].chart.isDatasetVisible(i)
                    );
                  let breakdown = '';

                  if (
                    visibleDatasets.length === 1 &&
                    point &&
                    'tip' in point &&
                    'fee' in point
                  ) {
                    const tip = point?.tip || 0;
                    const fee = point?.fee || 0;
                    const order_amount = point?.order_amount || 0;
                    breakdown = `Tip  : ${CURRENCY.convertToMainUnit(
                      tip
                    )}\nFee : ${CURRENCY.convertToMainUnit(fee)}
                    \nOrder Amount : ${CURRENCY.convertToMainUnit(order_amount)}`;
                  }

                  return breakdown;
                }
              : undefined,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
      line: {
        borderColor: chartStyles.chartLineColor,
        borderWidth: chartStyles.chartLineWidth,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: chartStyles.chartGridColor,
          borderColor: chartStyles.chartGridColor,
          drawTicks: false,
        },
        ticks: {
          precision: param === STATS.graphType.payments ? 0 : undefined,
          color: chartStyles.chartAxisTicksColor,
          padding: chartStyles.chartAxisTicksPadding,
          callback: (value) => {
            return Number(value) > 0 && param !== STATS.graphType.payments
              ? CURRENCY.getCompactNotation(Number(value))
              : value;
          },
        },
      },
      x: {
        type: 'timeseries',
        // min: format(new Date(dateFrom), 'dd MMM yyyy'),
        // max: format(new Date(dateTo), 'dd MMM yyyy'),
        time: {
          minUnit: 'second',
          unit: timeAxisUnit,
          stepSize: timeAxisStepSize,
          round: 'second',
          displayFormats: {
            minute: 'hh:mm a',
            hour: 'hh a',
            day: 'd MMM',
            month: 'MMM',
            year: 'YYYY',
            week: 'd MMM',
          },
          parser: (value: unknown) => {
            return new Date(value as string).valueOf();
          },
        },
        adapters: {
          date: {
            zone: timezone,
          },
        },
        grid: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
          drawBorder: false,
        },
        ticks: {
          autoSkip: false,
          source: 'data',
          color: chartStyles.chartAxisTicksColor,
          padding: chartStyles.chartAxisTicksPadding,
          maxTicksLimit: maxXTicksLimit,
        },
      },
    },
  };
};

export const customChartConfig: Record<STATS.IPaymentType, ICustomChartConfig> =
  {
    [STATS.IPaymentType.ALL]: {
      borderColor: '#4080EC',
      backgroundColor: 'rgba(227, 236, 253, 0.3)',
      order: 4,
      legendBgColor: '#F2F6FE',
      legendTextColor: '#0055E5',
      label: util.getPaymentTypeDisplayText(STATS.IPaymentType.ALL),
    },
    [STATS.IPaymentType.ACH]: {
      borderColor: '#4DCB9F',
      backgroundColor: 'rgba(206, 238, 227, 0.3)',
      legendBgColor: '#E7F8F2',
      legendTextColor: '#00452D',
      label: util.getPaymentTypeDisplayText(STATS.IPaymentType.ACH),
    },
    [STATS.IPaymentType.CATM]: {
      borderColor: '#7D6AF2',
      backgroundColor: 'rgba(229, 225, 252, 0.3)',
      legendBgColor: '#F6F5FE',
      legendTextColor: '#5138EE',
      label: util.getPaymentTypeDisplayText(STATS.IPaymentType.CATM),
    },
    [STATS.IPaymentType.DEBIT]: {
      borderColor: '#FDB640',
      backgroundColor: 'rgba(255, 233, 194, 0.3)',
      legendBgColor: '#FFF8E0',
      legendTextColor: '#985406',
      label: util.getPaymentTypeDisplayText(STATS.IPaymentType.DEBIT),
    },
    [STATS.IPaymentType.CREDIT]: {
      borderColor: '#1E90FF',  // Sky blue border
      backgroundColor: 'rgba(135, 206, 250, 0.3)',  // Light sky blue background
      legendBgColor: '#E0F7FA',
      legendTextColor: '#01579B',  // Dark blue text
      label: util.getPaymentTypeDisplayText(STATS.IPaymentType.CREDIT),
    },    
  };
